import { GestureConfig, GestureCallbacks } from '../types';

// Base sensitivity configuration
const DEFAULT_SENSITIVITY = {
  delta: 30,
  velocity: 200,
};

// Navigation preset for dialogs with prev/next functionality
const createNavigationPreset = (callbacks: GestureCallbacks): GestureConfig => ({
  horizontal: {
    enabled: !!(callbacks.onNext || callbacks.onPrev),
    onLeft: callbacks.onNext,
    onRight: callbacks.onPrev,
  },
  vertical: {
    enabled: !!(callbacks.onExpand || callbacks.onCollapseOrClose),
    onUp: callbacks.onExpand,
    onDown: callbacks.onCollapseOrClose,
  },
  sensitivity: DEFAULT_SENSITIVITY,
  preventConflicts: true,
});

// Expand-collapse preset for dialogs with expansion functionality
const createExpandCollapsePreset = (callbacks: GestureCallbacks): GestureConfig => ({
  horizontal: {
    enabled: false,
  },
  vertical: {
    enabled: !!(callbacks.onExpand || callbacks.onCollapseOrClose),
    onUp: callbacks.onExpand,
    onDown: callbacks.onCollapseOrClose,
  },
  sensitivity: DEFAULT_SENSITIVITY,
  preventConflicts: true,
});

// Close-only preset for simple dialogs
const createCloseOnlyPreset = (callbacks: GestureCallbacks): GestureConfig => ({
  horizontal: {
    enabled: false,
  },
  vertical: {
    enabled: !!callbacks.onClose,
    onDown: callbacks.onClose,
  },
  sensitivity: DEFAULT_SENSITIVITY,
  preventConflicts: true,
});

// Full preset with all gestures enabled
const createFullPreset = (callbacks: GestureCallbacks): GestureConfig => ({
  horizontal: {
    enabled: !!(callbacks.onNext || callbacks.onPrev),
    onLeft: callbacks.onNext,
    onRight: callbacks.onPrev,
  },
  vertical: {
    enabled: !!(callbacks.onExpand || callbacks.onCollapseOrClose || callbacks.onClose),
    onUp: callbacks.onExpand,
    onDown: callbacks.onCollapseOrClose || callbacks.onClose,
  },
  sensitivity: DEFAULT_SENSITIVITY,
  preventConflicts: true,
});

// Preset factory function
const createGesturePreset = (
  preset: 'navigation' | 'expand-collapse' | 'close-only' | 'full',
  callbacks: GestureCallbacks
): GestureConfig => {
  switch (preset) {
    case 'navigation':
      return createNavigationPreset(callbacks);
    case 'expand-collapse':
      return createExpandCollapsePreset(callbacks);
    case 'close-only':
      return createCloseOnlyPreset(callbacks);
    case 'full':
      return createFullPreset(callbacks);
    default:
      return createCloseOnlyPreset(callbacks);
  }
};

// Validation helper
const validateGestureConfig = (config: GestureConfig): GestureConfig => {
  const validatedConfig = { ...config };
  
  // Validate sensitivity ranges
  if (validatedConfig.sensitivity) {
    const { delta, velocity } = validatedConfig.sensitivity;
    
    if (delta < 10 || delta > 100) {
      console.warn('StandardDialogTemplate: delta should be between 10-100px for optimal gesture detection');
    }
    
    if (velocity < 50 || velocity > 1000) {
      console.warn('StandardDialogTemplate: velocity should be between 50-1000 for optimal gesture detection');
    }
  }
  
  return validatedConfig;
};

export {
  createNavigationPreset,
  createExpandCollapsePreset,
  createCloseOnlyPreset,
  createFullPreset,
  createGesturePreset,
  validateGestureConfig,
};
